import React, { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail, ChevronRight, ChevronDown, Video, Image as ImageIcon, MapPin, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "../assets/logo.svg";
import ococLogo from "../assets/icoc-logo.avif";
import { useContentStatus } from "./use-content-status";
import ContentSourceNotice from "@/components/ui/ContentSourceNotice";
import { fallbackSiteSettings } from "@/content/fallback-content";
import { getSiteSettings } from "@/services/sanity/content";
import { useTheme } from "./theme/useTheme";
import { useLanguage } from "./language/useLanguage";
import MaoriBackground from "@/components/ui/MaoriBackground";
import RegionalChurchesMarquee from "@/components/ui/RegionalChurchesMarquee";
import { regionalLinks } from "./regional-links";
import MobileBottomNav from "./MobileBottomNav";
import { isRouteActive, isStudyReaderRoute } from "./mobile-nav-config";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import PwaInstallModal from "@/components/ui/PwaInstallModal";
import PwaInstallBanner from "@/components/ui/PwaInstallBanner";

function isPrivateOrLocalUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();

        if (host === "localhost" || host === "127.0.0.1" || host === "::1" || host.endsWith(".local")) {
            return true;
        }

        if (host.startsWith("10.") || host.startsWith("192.168.")) {
            return true;
        }

        const private172 = /^172\.(1[6-9]|2\d|3[0-1])\./;
        return private172.test(host);
    } catch {
        return true;
    }
}

const Layout: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [address, setAddress] = useState(fallbackSiteSettings.address);
    const [contactEmail, setContactEmail] = useState(fallbackSiteSettings.contactEmail);
    const [adminUrl, setAdminUrl] = useState(fallbackSiteSettings.adminUrl);
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();
    const { language, toggleLanguage } = useLanguage();
    const { t } = useTranslation();
    const { activeFallbackMessage, clearStatus, reportStatus } = useContentStatus();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);

        const loadSettings = async () => {
            const result = await getSiteSettings();
            setAddress(result.data.address);
            setContactEmail(result.data.contactEmail);
            setAdminUrl(result.data.adminUrl);

            if (result.source === "fallback") {
                reportStatus("site-settings", result.source, result.error || "Live site settings are unavailable right now. Showing saved contact details instead.");
                return;
            }

            clearStatus("site-settings");
        };

        void loadSettings();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            clearStatus("site-settings");
        };
    }, [clearStatus, reportStatus]);

    const [mediaDropdownOpen, setMediaDropdownOpen] = useState(false);
    const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeAllMenus = () => {
        setIsMenuOpen(false);
        setMediaDropdownOpen(false);
    };

    // Close desktop dropdown on Escape key
    useEffect(() => {
        if (!mediaDropdownOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setMediaDropdownOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [mediaDropdownOpen]);

    const isMediaActive = location.pathname === "/videos" || location.pathname === "/gallery";

    const handleMouseEnterMedia = () => {
        if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
        setMediaDropdownOpen(true);
    };

    const handleMouseLeaveMedia = () => {
        dropdownTimeoutRef.current = setTimeout(() => {
            setMediaDropdownOpen(false);
        }, 150);
    };

    const isActive = (path: string) => isRouteActive(location.pathname, path);
    const isStudyReader = isStudyReaderRoute(location.pathname);
    const canShowStudioLink = Boolean(adminUrl) && (import.meta.env.DEV || !isPrivateOrLocalUrl(adminUrl));
    const kidsCheckinUrl = import.meta.env.VITE_KIDS_CHECKIN_URL || "https://kids.aucklandchurch.com";

    const { canInstall, isIos, triggerInstall } = usePwaInstall();
    const [showIosInstallModal, setShowIosInstallModal] = useState(false);

    const handleInstallClick = async () => {
        const outcome = await triggerInstall();
        if (outcome === "manual-instructions" || isIos) {
            setShowIosInstallModal(true);
        }
    };

    return (
        <div className="relative min-h-screen flex flex-col bg-warm-cream dark:bg-warm-dark-bg text-secondary dark:text-gray-100 font-sans transition-colors duration-300 overflow-x-hidden">
            {/* Subtle Maori art & church word cloud atmospheric background */}
            <MaoriBackground />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/95 dark:bg-warm-dark-surface/95 backdrop-blur-md border-b border-warm-border/70 dark:border-warm-dark-border transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Row 1: Logo + utility controls */}
                    <div className={`flex justify-between items-center transition-all duration-300 ${scrolled ? "py-2" : "py-3"}`}>
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center">
                            <Link to="/" className="flex items-center gap-3 group" onClick={closeAllMenus}>
                                <div className="relative overflow-hidden">
                                    <img className="h-12 w-auto relative z-10" src={logo} alt={t("nav.logoAlt")} width="48" height="48" />
                                    {/* Gradient sweep overlay - pure CSS, no framer-motion */}
                                    <div aria-hidden="true" className="absolute inset-0 z-20 pointer-events-none w-1/2 animate-logo-sweep" />
                                </div>
                                <span className="font-bold text-lg tracking-tight text-secondary dark:text-white hidden sm:block group-hover:text-primary transition-colors">
                                    Central Auckland Church of Christ
                                </span>
                            </Link>
                        </div>

                        {/* Utility controls - always visible */}
                        <div className="flex items-center gap-3">
                            <ContentSourceNotice source={activeFallbackMessage ? "fallback" : "sanity"} message={activeFallbackMessage} />

                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                aria-label={theme === "dark" ? t("nav.switchToLight") : t("nav.switchToDark")}
                            >
                                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                            </button>

                            {/* Language Toggle */}
                            <button
                                onClick={toggleLanguage}
                                className="text-xs font-bold tracking-wide text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors px-2 py-1 rounded border border-gray-200 dark:border-gray-700 hover:border-primary"
                                aria-label={language === "en" ? t("nav.switchToMaori") : t("nav.switchToEnglish")}
                            >
                                {language === "en" ? "MI" : "EN"}
                            </button>

                            {/* Plan Your Visit CTA - tablet & desktop */}
                            <Link
                                to="/visit"
                                onClick={closeAllMenus}
                                className="hidden md:inline-flex items-center justify-center px-4 py-2 border border-transparent text-xs font-bold rounded-full text-white bg-primary hover:bg-primary-hover transition-all shadow-sm hover:shadow whitespace-nowrap"
                            >
                                {t("nav.planYourVisit")}
                            </Link>
                        </div>
                    </div>

                    {/* Row 2: Nav links - tablet & desktop */}
                    <nav className="hidden md:flex items-center justify-center gap-4 lg:gap-6 border-t border-gray-100 dark:border-gray-800 py-2" aria-label={t("nav.mainNav")}>
                        <Link
                            to="/"
                            aria-current={isActive("/") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.home")}
                        </Link>
                        <Link
                            to="/visit"
                            aria-current={isActive("/visit") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/visit") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.visit")}
                        </Link>
                        <Link
                            to="/about"
                            aria-current={isActive("/about") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/about") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.about")}
                        </Link>
                        <Link
                            to="/events"
                            aria-current={isActive("/events") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/events") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.events")}
                        </Link>

                        {/* Media Dropdown */}
                        <div
                            className="relative"
                            onMouseEnter={handleMouseEnterMedia}
                            onMouseLeave={handleMouseLeaveMedia}
                        >
                            <button
                                type="button"
                                onClick={() => setMediaDropdownOpen((prev) => !prev)}
                                aria-expanded={mediaDropdownOpen}
                                aria-haspopup="true"
                                className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap inline-flex items-center gap-1 focus:outline-none cursor-pointer py-1 ${
                                    isMediaActive ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                                }`}
                            >
                                <span>{t("nav.media")}</span>
                                <ChevronDown size={13} className={`transition-transform duration-200 ${mediaDropdownOpen ? "rotate-180" : ""}`} />
                            </button>

                            {mediaDropdownOpen && (
                                <div
                                    className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-60 z-50 animate-fade-in"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-label={t("nav.media")}
                                >
                                    <div className="rounded-2xl bg-white/95 dark:bg-zinc-800/95 backdrop-blur-md border border-warm-border dark:border-warm-dark-border shadow-xl p-2 flex flex-col gap-1">
                                        <Link
                                            to="/videos"
                                            role="menuitem"
                                            aria-current={isActive("/videos") ? "page" : undefined}
                                            onClick={() => setMediaDropdownOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold tracking-wide transition-colors ${
                                                isActive("/videos")
                                                    ? "text-primary bg-green-50 dark:bg-zinc-700/50"
                                                    : "text-gray-700 dark:text-gray-200 hover:text-primary hover:bg-gray-50 dark:hover:bg-zinc-700/40"
                                            }`}
                                        >
                                            <Video size={16} className="text-primary shrink-0" />
                                            <div className="flex flex-col">
                                                <span>{t("nav.videos")}</span>
                                                <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">Sunday sermons & messages</span>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/gallery"
                                            role="menuitem"
                                            aria-current={isActive("/gallery") ? "page" : undefined}
                                            onClick={() => setMediaDropdownOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold tracking-wide transition-colors ${
                                                isActive("/gallery")
                                                    ? "text-primary bg-green-50 dark:bg-zinc-700/50"
                                                    : "text-gray-700 dark:text-gray-200 hover:text-primary hover:bg-gray-50 dark:hover:bg-zinc-700/40"
                                            }`}
                                        >
                                            <ImageIcon size={16} className="text-rose-500 shrink-0" />
                                            <div className="flex flex-col">
                                                <span>{t("nav.gallery")}</span>
                                                <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">Photos & Instagram moments</span>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Link
                            to="/resources"
                            aria-current={isActive("/resources") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/resources") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.resources")}
                        </Link>
                        <Link
                            to="/giving"
                            aria-current={isActive("/giving") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/giving") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.giving")}
                        </Link>
                        <Link
                            to="/contact"
                            aria-current={isActive("/contact") ? "page" : undefined}
                            className={`text-xs font-bold tracking-wide uppercase transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                                isActive("/contact") ? "text-primary" : "text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary"
                            }`}
                        >
                            {t("nav.contact")}
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-grow flex flex-col">
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="relative bg-secondary text-white overflow-hidden" role="contentinfo">
                {/* Top accent line */}
                <div className="h-px w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-60" aria-hidden="true" />

                {/* Main content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-[calc(6.5rem+max(0.5rem,env(safe-area-inset-bottom,0px)))] md:pb-10">
                    {/* Primary row: brand · nav · contact · socials */}
                    <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1.4fr_1fr]">
                        {/* Brand */}
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-3">
                                <img className="h-11 w-11 brightness-0 invert" src={logo} alt="" aria-hidden="true" width="44" height="44" />
                                <span className="font-bold text-base leading-snug tracking-tight">
                                    Central Auckland
                                    <br />
                                    Church of Christ
                                </span>
                            </div>
                            <p className="text-sm text-gray-300 leading-relaxed max-w-xs">{t("footer.tagline")}</p>
                            {/* Socials */}
                            <div className="flex items-center gap-3 mt-1">
                                <a
                                    href="https://www.facebook.com/pages/Auckland-Church-Central-Auckland-Church-of-Christ/409907289088953"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white/10 p-2.5 rounded-full text-white hover:bg-[#1877F2] transition-all hover:-translate-y-0.5"
                                    aria-label={t("footer.facebookAriaLabel")}
                                >
                                    <Facebook size={17} aria-hidden="true" />
                                </a>
                                <a
                                    href="https://www.instagram.com/icocauckland/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white/10 p-2.5 rounded-full text-white hover:bg-[#E4405F] transition-all hover:-translate-y-0.5"
                                    aria-label={t("footer.instagramAriaLabel")}
                                >
                                    <Instagram size={17} aria-hidden="true" />
                                </a>
                                <a
                                    href="https://www.youtube.com/@AucklandChurch"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white/10 p-2.5 rounded-full text-white hover:bg-[#FF0000] transition-all hover:-translate-y-0.5"
                                    aria-label={t("footer.youtubeAriaLabel")}
                                >
                                    <Youtube size={17} aria-hidden="true" />
                                </a>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <nav aria-label="Footer quick links">
                            <h3 className="mb-3 text-[11px] font-bold uppercase tracking-widest text-green-200">Site</h3>
                            <ul className="space-y-1">
                                {[
                                    { label: "Visit Us", to: "/visit" },
                                    { label: "Events", to: "/events" },
                                    { label: "Watch Videos", to: "/videos" },
                                    { label: "Photo Gallery", to: "/gallery" },
                                    { label: "Resources", to: "/resources" },
                                    { label: "Giving", to: "/giving" },
                                    { label: "About Us", to: "/about" },
                                    { label: "Contact Us", to: "/contact" },
                                ].map(({ label, to }) => (
                                    <li key={to}>
                                        <Link to={to} className="group flex items-center gap-1.5 text-sm py-1 text-gray-300 hover:text-white transition-colors">
                                            <ChevronRight size={13} className="text-green-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {/* Contact */}
                        <section aria-labelledby="footer-contact">
                            <h3 id="footer-contact" className="mb-4 text-[11px] font-bold uppercase tracking-widest text-green-200">
                                Contact
                            </h3>
                            <div className="space-y-4">
                                <a href={`mailto:${contactEmail}`} className="group flex items-start gap-3 text-sm py-2 text-gray-300 hover:text-white transition-colors">
                                    <div className="mt-0.5 shrink-0 rounded-full bg-white/10 p-1.5 group-hover:bg-primary transition-colors">
                                        <Mail size={14} />
                                    </div>
                                    <span className="break-all mt-0.5">{contactEmail}</span>
                                </a>
                                <div className="flex items-start gap-3 text-sm py-2 text-gray-300">
                                    <div className="mt-0.5 shrink-0 rounded-full bg-white/10 p-1.5">
                                        <MapPin size={14} />
                                    </div>
                                    <div className="mt-0.5">
                                        <p className="whitespace-pre-line">{address}</p>
                                        <p className="mt-1 text-xs text-gray-400">Charity Reg: CC33488</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ICOC badge */}
                        <section aria-labelledby="footer-network" className="flex flex-col gap-3">
                            <h3 id="footer-network" className="text-[11px] font-bold uppercase tracking-widest text-green-200">
                                Our Network
                            </h3>
                            <a
                                href="https://disciplestoday.org/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex flex-col items-start gap-2 rounded-2xl border border-white/10 bg-white/5 p-4 transition-all hover:border-green-300/40 hover:bg-white/10"
                            >
                                <img src={ococLogo} alt="" aria-hidden="true" className="h-10 w-auto object-contain" width="80" height="40" />
                                <span className="text-xs text-gray-300 leading-snug group-hover:text-white transition-colors">Disciples Today - Member of the International Churches of Christ</span>
                            </a>
                        </section>
                    </div>

                    {/* Regional churches marquee */}
                    <section aria-labelledby="footer-regional" className="mt-10 border-t border-white/10 pt-8">
                        <h3 id="footer-regional" className="mb-3 text-[11px] font-bold uppercase tracking-widest text-green-200">
                            Regional
                        </h3>
                        <RegionalChurchesMarquee links={regionalLinks} />
                    </section>

                    {/* Bottom bar */}
                    <div className="mt-10 border-t border-white/10 pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-gray-300">
                            {t("footer.copyright", { year: new Date().getFullYear() })}{" "}
                            <a href="https://swiftweb.co.nz" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white transition-colors">
                                SwiftWeb
                            </a>
                            .
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-300">
                            <Link to="/privacy-policy" className="hover:text-white transition-colors">
                                {t("footer.privacyPolicy")}
                            </Link>
                            <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                llms.txt
                            </a>
                            <a href={kidsCheckinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                {t("footer.kidsCheckin")}
                            </a>
                            <a href="https://aucklandchurch.elvanto.com.au/admin/?new_login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                Elvanto
                            </a>
                            {canShowStudioLink ? (
                                <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                    Studio
                                </a>
                            ) : null}
                        </div>
                    </div>
                </div>
            </footer>

            {/* Non-intrusive Install App Banner (< 768px / mobile) */}
            <PwaInstallBanner
                canInstall={canInstall && !isStudyReader}
                onInstallClick={handleInstallClick}
            />

            {/* iOS / Manual PWA Installation Guide Modal */}
            <PwaInstallModal
                isOpen={showIosInstallModal}
                onClose={() => setShowIosInstallModal(false)}
            />

            {/* Mobile-only Bottom Navigation & Sheet (< 768px / md:hidden) - suppressed during immersive study reading */}
            {!isStudyReader && (
                <MobileBottomNav
                    isMoreOpen={isMenuOpen}
                    onOpenMore={() => setIsMenuOpen(true)}
                    onCloseMore={() => setIsMenuOpen(false)}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    language={language}
                    onToggleLanguage={toggleLanguage}
                    canInstall={canInstall}
                    onInstallClick={handleInstallClick}
                />
            )}
        </div>
    );
};

export default Layout;
